import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Coins, Plus, Edit, Trash2, Loader2, RefreshCw, Search } from "lucide-react";

interface CurrencyItem {
  id: string | number;
  Id?: string | number;
  name?: string;
  CurrencyName?: string;
  code?: string;
  CurrencyCode?: string;
  symbol?: string;
  CurrencySymbol?: string;
  status?: boolean;
}

export function CurrencyPage() {
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<CurrencyItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<CurrencyItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [form, setForm] = useState({ name: "", code: "", symbol: "" });

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/Currency/List");
      const list = unwrapArray<CurrencyItem>(res);
      setCurrencies(list);
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load currencies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = currencies.filter((c) => {
    const name = (c.name || c.CurrencyName || "").toLowerCase();
    const code = (c.code || c.CurrencyCode || "").toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || code.includes(q);
  });

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.code || !form.symbol) {
      error("Validation Error", "All fields are required");
      return;
    }

    setActionLoading(true);
    try {
      await api.post("/api/Currency/Insert", {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        symbol: form.symbol.trim(),
      });
      success("Currency Created", "New currency created successfully");
      setAddOpen(false);
      setForm({ name: "", code: "", symbol: "" });
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Failed to create currency");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditOpen = (c: CurrencyItem) => {
    setEditItem(c);
    setForm({
      name: c.name || c.CurrencyName || "",
      code: c.code || c.CurrencyCode || "",
      symbol: c.symbol || c.CurrencySymbol || "",
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;

    setActionLoading(true);
    const id = editItem.id || editItem.Id;
    try {
      await api.put(`/api/Currency/Update/${id}`, {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        symbol: form.symbol.trim(),
      });
      success("Currency Updated", "Currency saved successfully");
      setEditItem(null);
      setForm({ name: "", code: "", symbol: "" });
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update currency");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    const id = deleteItem.id || deleteItem.Id;
    try {
      await api.delete(`/api/Currency/Delete/${id}`);
      success("Currency Deleted", "Currency deleted successfully");
      setDeleteItem(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete currency");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Supported Currencies
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure transaction and billing currencies, symbols, and ISO codes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setForm({ name: "", code: "", symbol: "" });
              setAddOpen(true);
            }}
            className="h-9 gap-1.5 shadow-sm"
          >
            <Plus className="size-4" />
            Add Currency
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          placeholder="Search currencies by name or ISO code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-9 text-sm"
        />
      </div>

      {/* Table with Skeleton Loading */}
      {loading ? (
        <TableSkeleton rows={4} columns={4} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>ISO Code</TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const name = c.name || c.CurrencyName || "Unnamed";
                const code = c.code || c.CurrencyCode || "N/A";
                const symbol = c.symbol || c.CurrencySymbol || "-";

                return (
                  <TableRow key={c.id || c.Id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="size-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                          <Coins className="size-4" />
                        </div>
                        <span className="font-semibold text-sm text-foreground">
                          {name}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <code className="px-2 py-0.5 rounded bg-muted font-mono text-xs font-semibold">
                        {code}
                      </code>
                    </TableCell>

                    <TableCell>
                      <span className="text-base font-medium text-foreground">
                        {symbol}
                      </span>
                    </TableCell>

                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleEditOpen(c)}
                        title="Edit Currency"
                      >
                        <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setDeleteItem(c)}
                        title="Delete Currency"
                      >
                        <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                    No currencies configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog: Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Add Currency</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Register a new currency for invoicing and pricing
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Currency Name *</Label>
              <Input
                required
                placeholder="US Dollar"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>ISO Code *</Label>
                <Input
                  required
                  placeholder="USD"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol *</Label>
                <Input
                  required
                  placeholder="$"
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Currency
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit */}
      <Dialog open={!!editItem} onOpenChange={(open) => !open && setEditItem(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">Edit Currency</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Update currency name, code, or symbol
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Currency Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>ISO Code *</Label>
                <Input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol *</Label>
                <Input
                  required
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600 dark:text-red-400">Delete Currency</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to remove{" "}
              <strong className="text-foreground">
                {deleteItem?.name || deleteItem?.CurrencyName} ({deleteItem?.code || deleteItem?.CurrencyCode})
              </strong>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
